/**
 * AdminPhotosReview — /admin/photos
 * Reviews pending photo submissions. Admin can approve (choosing a
 * slot: top/middle/bottom/header/sidebar/inline) or reject with reason.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, MapPin, User, Tag as TagIcon, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const SLOTS = ["top", "middle", "bottom", "header", "sidebar", "inline"];

export default function AdminPhotosReview() {
  const { user, token } = useAuth();
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/photos/pending?status=${status}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (token) load(); }, [token, status]);

  const approve = async (id, slot) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/api/photos/${id}/approve`, { slot },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Approved & published");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (optional, will be emailed):");
    if (reason === null) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/api/photos/${id}/reject`, { reason },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  if (!user?.is_admin && user?.role !== "admin") {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center">
        <h1 className="font-display text-3xl mb-2">Admin only</h1>
        <Link to="/" className="wofy-link">← Home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4" data-testid="admin-photos">
      <Link to="/" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-4">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline text-xs opacity-70">ADMIN</div>
          <h1 className="font-display text-3xl">Photo Submissions</h1>
        </div>
        <div className="flex gap-1">
          {["pending", "approved", "rejected", "all"].map(s => (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full text-xs ${status === s ? "text-white" : ""}`}
              style={{ background: status === s ? "var(--wofy-primary)" : "var(--wofy-surface)" }}
              data-testid={`filter-${s}`}
            >{s}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="opacity-60 text-center py-10"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="wofy-card !p-10 text-center opacity-70">No {status} submissions.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map(p => (
            <div key={p.id} className="wofy-card !p-4 space-y-3" data-testid={`submission-${p.id}`}>
              <img src={p.data_uri} alt={p.title} className="w-full max-h-64 object-cover rounded-lg" />
              <div>
                <div className="font-medium">{p.title}</div>
                {p.description && <p className="text-sm opacity-80 mt-1">{p.description}</p>}
              </div>
              <div className="text-xs opacity-70 space-y-1">
                <div><User className="w-3 h-3 inline mr-1" />{p.submitter_name} · {p.submitter_email}</div>
                {(p.city || p.state || p.country) && <div><MapPin className="w-3 h-3 inline mr-1" />{[p.city, p.state, p.country].filter(Boolean).join(", ")}</div>}
                {p.tags?.length > 0 && <div><TagIcon className="w-3 h-3 inline mr-1" />{p.tags.join(", ")}</div>}
                {p.target_type !== "general" && <div>Target: <code>{p.target_type}</code> / <code>{p.target_slug || p.target_id || "—"}</code></div>}
                {p.gps_lat != null && <div>GPS: {p.gps_lat.toFixed(4)}, {p.gps_lon?.toFixed(4)}</div>}
              </div>
              {status === "pending" && (
                <div className="flex gap-2 flex-wrap pt-2 border-t" style={{ borderColor: "var(--wofy-border)" }}>
                  <select id={`slot-${p.id}`} defaultValue="middle" className="px-2 py-1 rounded border text-xs" style={{ borderColor: "var(--wofy-border)" }}>
                    {SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => approve(p.id, document.getElementById(`slot-${p.id}`).value)}
                    disabled={busyId === p.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-xs disabled:opacity-40"
                    style={{ background: "#166534" }}
                    data-testid={`approve-${p.id}`}
                  >
                    {busyId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Approve
                  </button>
                  <button onClick={() => reject(p.id)} disabled={busyId === p.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs" style={{ borderColor: "#dc2626", color: "#dc2626" }}
                    data-testid={`reject-${p.id}`}
                  >
                    <XCircle className="w-3 h-3" /> Reject
                  </button>
                </div>
              )}
              {status !== "pending" && (
                <div className="text-xs opacity-70 pt-2 border-t" style={{ borderColor: "var(--wofy-border)" }}>
                  Status: <b>{p.status}</b>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
